/**
 * cache/hydrate-sentinels.ts · post-restore sentinel installation.
 *
 * Spec anchor: file 05 §L2540+ (T3035).
 *
 * After `persistQueryClientRestore`, every restored `isPaused` mutation
 * is still queued in the `MutationCache` but its `mutationFn` hasn't
 * yet been invoked. The SDK's inflight gate (`onMutate` serialisation)
 * keys by scope; a paused mutation needs a Promise placeholder on the
 * inflight Map so a fresh in-session mutation in the same scope blocks
 * until the paused one drains.
 *
 * `installPausedMutationSentinels(qc, inflight)` walks the cache,
 * synthesises a sentinel Promise per paused mutation, and stores it on
 * the inflight Map under the spec's scope key. When the resumed
 * mutation settles (success OR error), the sentinel resolves and the
 * waiting mutation proceeds.
 */

export interface PausedMutationLike {
  readonly options: { readonly mutationKey?: readonly unknown[] };
  state: { isPaused: boolean };
  /** Promise that resolves when the mutation finally settles. */
  continue(): Promise<unknown>;
}

export interface SentinelMutationCache {
  getAll(): ReadonlyArray<PausedMutationLike>;
}

export interface SentinelInflightMap {
  has(scopeKey: string): boolean;
  set(scopeKey: string, p: Promise<unknown>): void;
  delete(scopeKey: string): void;
}

/**
 * Walk the mutation cache, install sentinel Promises on the inflight
 * Map for every paused mutation. Sentinel resolves when the mutation's
 * `continue()` promise settles (success or error).
 *
 * Returns the count of sentinels installed.
 */
export function installPausedMutationSentinels(
  cache: SentinelMutationCache,
  inflight: SentinelInflightMap,
): number {
  let installed = 0;
  for (const m of cache.getAll()) {
    if (m.state.isPaused !== true) continue;
    const key = m.options.mutationKey;
    if (key === undefined || key.length === 0) continue;
    const scopeKey = String(key[0]);
    if (inflight.has(scopeKey)) continue; // don't double-install

    // We don't await continue() — we install the sentinel and let it
    // resolve in the background; the waiting mutation `await`s the
    // sentinel via the inflight Map.
    const sentinel = m
      .continue()
      .catch(() => undefined)
      .finally(() => {
        inflight.delete(scopeKey);
      });
    inflight.set(scopeKey, sentinel);
    installed += 1;
  }
  return installed;
}

// ============================================================================
// T1005 · post-hydrate inflight sentinel install (QC-keyed API)
// ============================================================================

/**
 * T1005 shape: walks a `QueryClient`'s mutation cache for `isPaused`
 * entries whose `options.scope.id` is set, and installs a sentinel
 * Promise on the inflight Map keyed by `scope.id`. The sentinel
 * resolves when the mutation transitions to `'success'` or `'error'`;
 * eviction is compare-by-reference so a later live mutation can safely
 * overwrite the slot without clobbering the prior session's settlement
 * path.
 *
 * Spec anchor: file 05 §2542.
 */

export interface T1005MutationCacheLike {
  getAll(): ReadonlyArray<T1005MutationLike>;
  subscribe(
    listener: (event: { readonly type: string; readonly mutation: T1005MutationLike }) => void,
  ): () => void;
}

export interface T1005MutationLike {
  readonly options: { readonly scope?: { readonly id?: string } };
  readonly state: {
    readonly isPaused: boolean;
    readonly status: 'pending' | 'success' | 'error' | 'idle';
  };
}

export interface T1005Qc {
  getMutationCache(): T1005MutationCacheLike;
}

export interface T1005InflightMap {
  get(key: string): Promise<void> | undefined;
  set(key: string, p: Promise<void>): void;
  delete(key: string): void;
}

/**
 * Walk the QC's mutation cache, install a sentinel per paused mutation
 * whose scope.id is set. Returns the count installed.
 */
export function installInflightSentinels(qc: T1005Qc, inflight: T1005InflightMap): number {
  const cache = qc.getMutationCache();
  const paused = cache.getAll().filter((m) => m.state.isPaused === true);
  let installed = 0;
  for (const m of paused) {
    const scopeId = m.options.scope?.id;
    if (scopeId === undefined) continue;

    let resolveSentinel: () => void = (): void => {};
    const sentinel = new Promise<void>((resolve) => {
      resolveSentinel = resolve;
    });
    inflight.set(scopeId, sentinel);

    const unsubscribe = cache.subscribe((event) => {
      if (event.mutation !== m) return;
      if (
        event.type === 'updated' &&
        (m.state.status === 'success' || m.state.status === 'error')
      ) {
        // Compare-by-reference: only evict if the slot still holds
        // THIS sentinel. A later live mutation may have overwritten.
        if (inflight.get(scopeId) === sentinel) inflight.delete(scopeId);
        resolveSentinel();
        unsubscribe();
      }
    });
    installed += 1;
  }
  return installed;
}
